import { usePublicBootstrap } from "./usePublicBootstrap";
import BookingFlow from "./pages/booking/BookingFlow";
import { BookingDraftProvider } from "./pages/booking/BookingDraftContext";
import { WidgetSessionExpiredOverlay } from "./components/WidgetSessionExpiredOverlay";
import { useTranslation } from "react-i18next";

/**
 * Read the `?staff=<id>` query parameter injected by embed.js
 * from the trigger element's `data-boona-staff` attribute.
 */
function readStaffParam(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get("staff") || null;
}

/**
 * Read the `?service=<id>` query parameter injected by embed.js
 * from the trigger element's `data-boona-service` attribute.
 */
function readServiceParam(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get("service") || null;
}

/**
 * Read the `?category=<id>` query parameter injected by embed.js
 * from the trigger element's `data-boona-category` attribute.
 */
function readCategoryParam(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get("category") || null;
}

/**
 * Extract the widget_host slug from the current hostname.
 * e.g. "w1033.boona.app" → "w1033"
 */
function extractWidgetHost(): string | null {
  const hostname = window.location.hostname;
  const match = hostname.match(/^(w\d+)\./);
  return match ? match[1] : null;
}

/**
 * Widget bootstrap flow:
 * 1. Extracts widgetHost from the subdomain (e.g. "w1033" from "w1033.boona.app")
 * 2. POSTs { widget_host } to /public/sessions — backend resolves to company_id
 * 3. Stores auth token, fetches company settings
 * 4. Passes resolved companyId to BookingDraftProvider for all downstream API calls
 */

export function Widget({
  apiBase = import.meta.env.VITE_API_URL ?? "https://boona.app",
  widgetHost,
}: {
  apiBase?: string;
  widgetHost?: string;
}) {
  const { t } = useTranslation();
  const resolvedHost = widgetHost ?? extractWidgetHost();

  if (!resolvedHost) {
    return (
      <pre style={{ whiteSpace: "pre-wrap" }}>
        {t(
          "widget.bootstrap.missing_host",
          'Bootstrap error: Could not determine widget host from hostname "{{hostname}}"',
          { hostname: window.location.hostname },
        )}
      </pre>
    );
  }

  const { ready, error, companyId } = usePublicBootstrap({ apiBase, widgetHost: resolvedHost });

  return (
    <div
      className="font-sans"
    >
      {error ? (
        <pre style={{ whiteSpace: "pre-wrap" }}>
          {t("widget.bootstrap.error_prefix", "Bootstrap error")}: {String(error)}
        </pre>
      ) : ready && companyId ? (
        <BookingDraftProvider companyId={companyId}>
          <BookingFlow preselectedStaffId={readStaffParam()} preselectedServiceId={readServiceParam()} preselectedCategoryId={readCategoryParam()} />
        </BookingDraftProvider>
      ) : (
        <div>{t("widget.bootstrap.authorizing", "Authorizing...")}</div>
      )}
      <WidgetSessionExpiredOverlay />
    </div>
  );
}
